import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, TrendingUp, Users, Truck, Package, CheckCircle2, AlertTriangle, FileCheck2 } from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const COLORS = {
  montado: "#10b981", semi_montado: "#f59e0b", incidencia: "#ef4444", cancelado: "#71717a",
  tiempo: "#3b82f6", medida: "#f97316", otros: "#a855f7",
};

const CIERRE_LABEL = {
  montado: "Montado", semi_montado: "Semi", incidencia: "Incidencia", cancelado: "Cancelado",
};

function KPI({ icon: Icon, label, value, sub, color = "amber", testId }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={testId}>
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded bg-${color}-500/20 text-${color}-300 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function fechaMenos(dias) {
  const d = new Date(); d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

export default function Productividad() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState(fechaMenos(90));
  const [hasta, setHasta] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productividad", { params: { desde, hasta } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [desde, hasta]);

  if (loading && !data) return <div className="p-8 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;

  const totalCierres = data.cierres_montaje.reduce((a, b) => a + b.n, 0);
  const totalIncidencias = data.tipos_incidencia.reduce((a, b) => a + b.n, 0);

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader title="Productividad y estadísticas"
        subtitle={`Periodo ${desde} → ${hasta}`} />

      {/* Filtro de fechas */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <Label className="text-xs text-zinc-500">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            data-testid="filtro-desde" className="bg-zinc-900 border-zinc-800 w-40" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            data-testid="filtro-hasta" className="bg-zinc-900 border-zinc-800 w-40" />
        </div>
        {[7, 30, 90, 365].map((d) => (
          <button key={d} type="button" onClick={() => { setDesde(fechaMenos(d)); setHasta(new Date().toISOString().slice(0, 10)); }}
            data-testid={`preset-${d}d`}
            className="text-xs px-3 py-2 rounded border border-zinc-800 bg-zinc-900 hover:border-amber-500/50 text-zinc-300">{d}d</button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpis">
        <KPI icon={Truck} label="Rutas" value={data.rutas.total}
          sub={`${data.rutas.finalizadas} finalizadas · ${data.rutas.en_curso} en curso`} color="amber" testId="kpi-rutas" />
        <KPI icon={CheckCircle2} label="Montajes completados" value={data.cierres_montaje.find(c => c.estado === "montado")?.n || 0}
          sub={`${totalCierres} reportes totales`} color="emerald" testId="kpi-montados" />
        <KPI icon={AlertTriangle} label="Incidencias" value={totalIncidencias}
          sub={`${totalCierres ? Math.round(totalIncidencias / totalCierres * 100) : 0}% del total`} color="red" testId="kpi-incidencias" />
        <KPI icon={TrendingUp} label="Tiempo medio" value={data.tiempo_medio_montaje_min ? `${data.tiempo_medio_montaje_min}'` : "—"}
          sub="por montaje" color="cyan" testId="kpi-tiempo" />
      </div>

      {/* Pipeline reporte */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-6">
        <div className="text-xs font-semibold mb-3 flex items-center gap-1.5">
          <FileCheck2 className="w-3.5 h-3.5 text-amber-400" /> Pipeline de reportes
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div data-testid="pipe-pendientes-validar">
            <div className="text-[10px] uppercase text-amber-300">Pendientes validar</div>
            <div className="text-xl font-bold">{data.pipeline_reporte.pendientes_validar}</div>
          </div>
          <div data-testid="pipe-pendiente-envio">
            <div className="text-[10px] uppercase text-cyan-300">Pdte enviar al cliente</div>
            <div className="text-xl font-bold">{data.pipeline_reporte.aprobados_pendiente_envio}</div>
          </div>
          <div data-testid="pipe-enviados">
            <div className="text-[10px] uppercase text-violet-300">Enviados</div>
            <div className="text-xl font-bold">{data.pipeline_reporte.enviados}</div>
          </div>
          <div data-testid="pipe-albaranados">
            <div className="text-[10px] uppercase text-emerald-300">Albaranados</div>
            <div className="text-xl font-bold">{data.pipeline_reporte.albaranados}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Cierres montaje */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-xs font-semibold mb-2">Cierres de montaje</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.cierres_montaje.filter(d => d.n > 0)} dataKey="n" nameKey="estado"
                cx="50%" cy="50%" outerRadius={80} label={(e) => `${CIERRE_LABEL[e.estado]}: ${e.n}`}>
                {data.cierres_montaje.map((entry) => <Cell key={entry.estado} fill={COLORS[entry.estado]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tipos incidencia */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-xs font-semibold mb-2">Tipos de incidencia</div>
          {totalIncidencias === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm italic">Sin incidencias en este periodo 🎉</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.tipos_incidencia}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="tipo" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a" }} />
                <Bar dataKey="n">
                  {data.tipos_incidencia.map((entry) => <Cell key={entry.tipo} fill={COLORS[entry.tipo]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Serie diaria */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 lg:col-span-2">
          <div className="text-xs font-semibold mb-2">Montajes por día (últimos 30 días)</div>
          {data.serie_diaria.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm italic">Sin montajes finalizados aún</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.serie_diaria}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="fecha" stroke="#71717a" fontSize={10} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a" }} />
                <Line type="monotone" dataKey="montajes" stroke="#f59e0b" strokeWidth={2} dot={{ fill: "#f59e0b", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Ranking montadores */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-amber-400" /> Ranking montadores
          </div>
          {data.ranking_montadores.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm italic">Sin datos</div>
          ) : (
            <table className="w-full text-xs" data-testid="ranking-montadores">
              <thead className="text-[10px] uppercase text-zinc-500">
                <tr><th className="text-left py-1">Montador</th><th>Total</th><th className="text-emerald-300">✓</th><th className="text-amber-300">⚠</th><th className="text-red-300">✗</th></tr>
              </thead>
              <tbody>
                {data.ranking_montadores.map((m) => (
                  <tr key={m.usuario_id} className="border-t border-zinc-800/60">
                    <td className="py-1.5 font-medium">{m.nombre}</td>
                    <td className="text-center font-bold">{m.total}</td>
                    <td className="text-center text-emerald-300">{m.montado || 0}</td>
                    <td className="text-center text-amber-300">{m.semi_montado || 0}</td>
                    <td className="text-center text-red-300">{(m.incidencia || 0) + (m.cancelado || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top clientes */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5 text-amber-400" /> Top clientes (por peticiones)
          </div>
          {data.top_clientes.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm italic">Sin datos</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {data.top_clientes.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-800/60">
                    <td className="py-1.5">{i + 1}. {c.nombre}</td>
                    <td className="text-right font-bold text-amber-300">{c.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
